import type { CellContext } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import formatFinancialValue from "components/company/financials/formatFinancialValue";
import Table from "components/ui/Table";
import { TableBreakpoint } from "components/ui/Table/interface";
import classNames from "components/ui/classNames";
import numeral from "numeral";
import type { IAmericanCompanyTotalCompensationByYear, IAmericanReitTotalCompensationByYear } from "types/graphqlTypes";

interface IAmericanTotalCompensations {
  totalCompensations: (IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear)[];
  isREIT: boolean;
}

export default function AmericanTotalCompensations({ totalCompensations, isREIT }: IAmericanTotalCompensations) {
  const columnHelper = createColumnHelper<
    IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear
  >();

  const arrayOfColumns = [
    columnHelper.accessor("fiscalYear", {
      header: "Year",
      meta: {
        header: {
          className: "border-r border-slate-500 bg-slate-600 py-1.5 px-3 text-sm font-semibold text-white",
        },
        cell: {
          className:
            "font-monospace font-medium text-sm border-l-0 border-t border-slate-200 border-r lg:border-gray-500 lg:text-center lg:text-white lg:bg-slate-600",
        },
      },
    }),
    columnHelper.accessor("totalExecutiveCompensation", {
      header: "Executive Officers",
      cell: formatCellValue,
      meta: {
        breakpoint: TableBreakpoint.Large,
        header: {
          className:
            "hidden border-r border-slate-200 bg-slate-100 py-1.5 px-3 text-sm font-semibold text-slate-600 lg:table-cell",
        },
        cell: {
          className:
            "hidden font-monospace font-medium text-sm border border-l-0 border-b-0 text-slate-600 lg:text-center lg:border-slate-200 lg:table-cell",
        },
      },
    }),
    columnHelper.accessor("totalBoardOfDirectorsCompensation", {
      header: "Board of Directors",
      cell: formatCellValue,
      meta: {
        breakpoint: TableBreakpoint.Large,
        header: {
          className:
            "hidden border-r border-slate-200 bg-slate-100 py-1.5 px-3 text-sm font-semibold text-slate-600 lg:table-cell",
        },
        cell: {
          className:
            "hidden font-monospace font-medium text-sm border border-l-0 border-b-0 text-slate-600 lg:text-center lg:border-slate-200 lg:table-cell",
        },
      },
    }),
    columnHelper.accessor("totalCompensation", {
      header: "Total",
      cell: formatCellValue,
      meta: {
        breakpoint: TableBreakpoint.Medium,
        header: {
          className: "hidden bg-sky-100 py-1.5 px-3 text-sm font-semibold text-blue-600 md:table-cell",
        },
        cell: {
          className:
            "hidden font-monospace font-medium text-sm border border-l-0 border-b-0 text-blue-600 md:text-center md:border-slate-200 md:table-cell",
        },
      },
    }),

    // Conditional Columns
    columnHelper.accessor("netIncome", {
      header: "Net Inc.",
      cell: formatFinancialCellValue,
      meta: {
        isHidden: isREIT,
        breakpoint: TableBreakpoint.Small,
        header: {
          tooltip: "Revenue - Discounts",
          className:
            "hidden border-l border-green-200 bg-green-50 py-1.5 px-3 text-sm font-semibold tracking-wide text-green-700 sm:table-cell",
        },
        cell: {
          className: (props: CellContext<IAmericanCompanyTotalCompensationByYear, number>) => {
            return classNames(
              "hidden font-monospace font-medium text-sm border border-l-0 border-b-0 sm:text-center sm:border-slate-200 sm:table-cell",
              {
                "text-green-700": props.row.original.netIncome > 0,
                "text-red-500": props.row.original.netIncome < 0,
              },
            );
          },
        },
      },
    }),
    columnHelper.accessor("percentOfNetIncome", {
      header: "% Net Inc.",
      cell: (props) =>
        formatPercentageOfNetIncome(props as CellContext<IAmericanCompanyTotalCompensationByYear, number>),
      meta: {
        isHidden: isREIT,
        breakpoint: TableBreakpoint.Small,
        header: {
          className:
            "hidden border-l text-sm font-semibold tracking-wide bg-orange-50 text-yellow-700 border-b border-slate-200 sm:table-cell",
        },
        cell: {
          className: getClassNameForPercentageOfNetIncome,
        },
      },
    }),
    columnHelper.accessor("fundsFromOperations", {
      header: "FFO",
      cell: formatFinancialCellValue,
      meta: {
        isHidden: !isREIT,
        breakpoint: TableBreakpoint.Small,
        header: {
          tooltip: "Funds from operations",
          className:
            "hidden border-l border-green-200 bg-green-50 py-1.5 px-3 text-sm font-semibold tracking-wide text-green-700 sm:table-cell",
        },
        cell: {
          className: (props: CellContext<IAmericanReitTotalCompensationByYear, number>) => {
            return classNames(
              "hidden font-monospace font-medium text-sm border border-l-0 border-b-0 sm:text-center sm:border-slate-200 sm:table-cell",
              {
                "text-green-700": props.row.original.fundsFromOperations > 0,
                "text-red-500": props.row.original.fundsFromOperations < 0,
              },
            );
          },
        },
      },
    }),
    columnHelper.accessor("percentOfFundsFromOperations", {
      header: "% FFO",
      cell: (props) => formatPercentageOfFFO(props as CellContext<IAmericanReitTotalCompensationByYear, number>),
      meta: {
        isHidden: !isREIT,
        breakpoint: TableBreakpoint.Small,
        header: {
          className:
            "hidden border-l text-sm font-semibold tracking-wide bg-orange-50 text-yellow-700 border-b border-slate-200 sm:table-cell",
        },
        cell: {
          className: getClassNameForPercentageOfFFO,
        },
      },
    }),

    columnHelper.accessor("revenue", {
      header: "Revenue",
      cell: formatFinancialCellValue,
      meta: {
        breakpoint: TableBreakpoint.Small,
        header: {
          tooltip: "Income from Products and Service",
          className:
            "hidden border-l bg-green-50 py-1.5 px-3 text-sm font-semibold tracking-wide text-green-700 border-b border-slate-200 sm:table-cell",
        },
        cell: {
          className:
            "hidden font-monospace font-medium text-sm border border-b-0 text-green-700 sm:text-center sm:border-slate-200 sm:table-cell",
        },
      },
    }),
    columnHelper.accessor("percentageOfRevenue", {
      header: "% Revenue",
      cell: formatPercentageOfRevenue,
      meta: {
        breakpoint: TableBreakpoint.Small,
        header: {
          className:
            "hidden border-l text-sm font-semibold tracking-wide bg-orange-50 text-yellow-700 border-b border-slate-200 sm:table-cell",
        },
        cell: {
          className: getClassNameForPercentageOfRevenue,
        },
      },
    }),
  ];

  return (
    <Table<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear>
      columns={arrayOfColumns}
      data={totalCompensations}
    />
  );
}

function formatCellValue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  return numeral(props.getValue()).format("0,0").replace(/,/g, ".");
}

function formatFinancialCellValue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  return formatFinancialValue(props.getValue());
}

function formatPercentageOfNetIncome(props: CellContext<IAmericanCompanyTotalCompensationByYear, number>) {
  const value = props.row.original.netIncome === 0 ? 0 : props.getValue() * 100;
  let formattedRatio = `${(value || 0).toFixed(2).replace(".", ",")}%`;

  if ((value || 0) >= 10) {
    formattedRatio = `${Math.round(value || 0)}%`;
  }

  if ((value || 0) < 0 || props.row.original.netIncome < 0) {
    formattedRatio = "P";
  }

  return formattedRatio;
}

function formatPercentageOfFFO(props: CellContext<IAmericanReitTotalCompensationByYear, number>) {
  const value = props.row.original.fundsFromOperations === 0 ? 0 : props.getValue() * 100;
  let formattedRatio = `${(value || 0).toFixed(2).replace(".", ",")}%`;

  if ((value || 0) >= 10) {
    formattedRatio = `${Math.round(value || 0)}%`;
  }

  if ((value || 0) < 0 || props.row.original.fundsFromOperations < 0) {
    formattedRatio = "P";
  }

  return formattedRatio;
}

function formatPercentageOfRevenue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  const value = props.row.original.revenue === 0 ? 0 : props.getValue() * 100;

  return `${(value || 0) >= 10 ? Math.round(value || 0) : (value || 0).toFixed(2).replace(".", ",")}%`;
}

function getClassNameForPercentageOfNetIncome(props: CellContext<IAmericanCompanyTotalCompensationByYear, number>) {
  if (props.row.original.netIncome < 0) {
    return "hidden bg-red-50 text-red-500 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  const value = props.row.original.netIncome === 0 ? 0 : props.getValue() * 100;
  if (value >= 10) {
    return "hidden bg-red-50 text-red-500 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  if (value < 10 && value > 3) {
    return "hidden bg-orange-50 text-yellow-700 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  if (value <= 3) {
    return "hidden bg-green-50 text-green-700 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  return "hidden sm:table-cell";
}

function getClassNameForPercentageOfFFO(props: CellContext<IAmericanReitTotalCompensationByYear, number>) {
  if (props.row.original.fundsFromOperations < 0) {
    return "hidden bg-red-50 text-red-500 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  const value = props.row.original.fundsFromOperations === 0 ? 0 : props.getValue() * 100;
  if (value >= 10) {
    return "hidden bg-red-50 text-red-500 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  if (value < 10 && value > 3) {
    return "hidden bg-orange-50 text-yellow-700 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  if (value <= 3) {
    return "hidden bg-green-50 text-green-700 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  return "hidden sm:table-cell";
}

function getClassNameForPercentageOfRevenue(
  props: CellContext<IAmericanCompanyTotalCompensationByYear | IAmericanReitTotalCompensationByYear, number>,
) {
  const value = props.row.original.revenue === 0 ? 0 : props.getValue() * 100;

  if (value >= 2) {
    return "hidden bg-red-50 text-red-500 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  if (value < 2 && value > 0.5) {
    return "hidden bg-orange-50 text-yellow-700 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  if (value <= 0.5) {
    return "hidden bg-green-50 text-green-700 text-sm text-center font-medium font-monospace border-t border-slate-200 sm:table-cell";
  }

  return "hidden sm:table-cell";
}
